import { msalInstance } from '../index';
import { loginRequest } from '../authConfig';

export class AuthValidation {
    static validateEnvironment() {
        const requiredEnvVars = [
            'REACT_APP_TENANT_ID',
            'REACT_APP_APP_CLIENT_ID', 
            'REACT_APP_OPENAPI_CLIENT_ID',
            'REACT_APP_BACKEND_URL'
        ];

        const missing = requiredEnvVars.filter(envVar => !process.env[envVar]);
        
        if (missing.length > 0) {
            console.error('❌ Missing required environment variables:', missing);
            return false;
        }

        console.log('✅ All required environment variables are present');
        return true;
    }

    static validateMsalConfig() {
        try {
            const config = msalInstance.getConfiguration();
            console.log('📋 MSAL Configuration:', {
                clientId: config.auth.clientId,
                authority: config.auth.authority,
                redirectUri: config.auth.redirectUri,
                cacheLocation: config.cache.cacheLocation
            });

            if (!config.auth.clientId || !config.auth.authority) {
                console.error('❌ Invalid MSAL configuration');
                return false;
            }

            console.log('✅ MSAL configuration is valid');
            return true;
        } catch (error) {
            console.error('❌ Error validating MSAL config:', error);
            return false;
        }
    }

    static async validateActiveAccount() {
        const account = msalInstance.getActiveAccount();
        
        if (!account) {
            console.warn('⚠️ No active account found');
            const allAccounts = msalInstance.getAllAccounts();
            
            if (allAccounts.length > 0) {
                console.log('🔄 Setting first available account as active');
                msalInstance.setActiveAccount(allAccounts[0]);
                return allAccounts[0];
            }
            
            console.error('❌ No accounts available');
            return null;
        }

        console.log('✅ Active account found:', {
            username: account.username,
            name: account.name,
            localAccountId: account.localAccountId
        });

        return account;
    }

    static async validateTokenAcquisition() {
        try {
            const account = await this.validateActiveAccount();
            if (!account) {
                throw new Error('No active account');
            }

            const request = {
                ...loginRequest,
                account: account
            };

            console.log('🔑 Testing token acquisition with request:', {
                scopes: request.scopes,
                account: request.account.username
            });

            const response = await msalInstance.acquireTokenSilent(request);
            
            console.log('✅ Token acquisition successful:', {
                tokenType: response.tokenType,
                expiresOn: response.expiresOn,
                scopes: response.scopes,
                account: response.account.username
            });

            return response.accessToken;
        } catch (error) {
            console.error('❌ Token acquisition failed:', error);
            console.error('Error details:', {
                errorCode: error.errorCode,
                errorMessage: error.errorMessage,
                subError: error.subError
            });
            throw error;
        }
    }

    static async runFullValidation() {
        console.log('🔍 Running full authentication validation...');
        
        const results = {
            environment: false,
            msalConfig: false,
            activeAccount: false,
            tokenAcquisition: false
        };

        try {
            // Step 1: Validate environment
            results.environment = this.validateEnvironment();
            
            // Step 2: Validate MSAL config
            results.msalConfig = this.validateMsalConfig();
            
            // Step 3: Validate active account
            const account = await this.validateActiveAccount();
            results.activeAccount = !!account;
            
            // Step 4: Validate token acquisition (only if previous steps passed)
            if (results.environment && results.msalConfig && results.activeAccount) {
                try {
                    await this.validateTokenAcquisition();
                    results.tokenAcquisition = true;
                } catch (error) {
                    console.error('Token acquisition validation failed:', error);
                }
            }

            console.log('📊 Validation Results:', results);
            
            const allPassed = Object.values(results).every(result => result === true);
            
            if (allPassed) {
                console.log('🎉 All authentication validations passed!');
            } else {
                console.error('❌ Some authentication validations failed');
            }

            return results;
        } catch (error) {
            console.error('❌ Validation process failed:', error);
            return results;
        }
    }
}

// Export for use in browser console
window.AuthValidation = AuthValidation;